/**
 * MaturityAlertModal.jsx
 *
 * Pops up on every dashboard load showing:
 * - Hedge contracts maturing today or past due
 * - Receivables overdue or due today
 * - Payables overdue or due today
 *
 * USAGE in Dashboard.jsx:
 *   import MaturityAlertModal from "./MaturityAlertModal";
 *   // Add after </header>:
 *   <MaturityAlertModal
 *     hedges={hedges}
 *     receivables={receivables}
 *     payables={payables}
 *     companyId={companyId}
 *     onDataRefresh={fetchUserAndProfile}
 *   />
 *
 * REQUIRED SQL (run once in Supabase SQL Editor):
 *   ALTER TABLE hedge_positions
 *     ADD COLUMN IF NOT EXISTS status       TEXT DEFAULT 'Active',
 *     ADD COLUMN IF NOT EXISTS settled_date DATE,
 *     ADD COLUMN IF NOT EXISTS settled_rate NUMERIC,
 *     ADD COLUMN IF NOT EXISTS notes        TEXT;
 *   UPDATE hedge_positions SET status = 'Active' WHERE status IS NULL;
 *   UPDATE receivables SET status = 'Outstanding' WHERE status IS NULL;
 *   UPDATE payables    SET status = 'Outstanding' WHERE status IS NULL;
 */

import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { X, AlertTriangle, CheckCircle, RefreshCw, Clock, ChevronDown, ChevronUp } from "lucide-react";

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const ARCHIVED_STATUSES = ["Settled", "Rolled Over", "Cancelled", "Extended"];

// ── HELPERS ───────────────────────────────────────────────────────────────────
const parseAmt = (v) => parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;
const todayDate = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  : "—";

const fmtAmt = (n, currency = "INR") => {
  const symbols = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
  const sym = symbols[currency] || (currency + " ");
  const num = Number(n) || 0;
  if (currency === "INR") {
    if (num >= 10000000) return `${sym}${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000)   return `${sym}${(num / 100000).toFixed(2)}L`;
    if (num >= 1000)     return `${sym}${(num / 1000).toFixed(2)}K`;
    return `${sym}${num.toFixed(0)}`;
  }
  if (num >= 1000000) return `${sym}${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000)    return `${sym}${(num / 1000).toFixed(2)}K`;
  return `${sym}${num.toLocaleString()}`;
};

function toISO(ddmmyyyy) {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return null;
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateInput(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
}

const inputStyle = {
  width: "100%", padding: "7px 10px", borderRadius: 6,
  border: "1px solid #2a2d3a", background: "#1a1d26",
  color: "#f1f5f9", fontSize: 12, boxSizing: "border-box",
};

// ── HEDGE ACTION PANEL ────────────────────────────────────────────────────────
function HedgeActionPanel({ hedge, onDone }) {
  const [action,     setAction]     = useState("");
  const [rollDate,   setRollDate]   = useState("");
  const [rollRate,   setRollRate]   = useState("");
  const [settleAmt,  setSettleAmt]  = useState("");
  const [settleDate, setSettleDate] = useState("");
  const [note,       setNote]       = useState("");
  const [saving,     setSaving]     = useState(false);
  const [errMsg,     setErrMsg]     = useState("");

  const handleSubmit = async () => {
    if (!action) return;
    if ((action === "rolled" || action === "extended") && !rollDate) {
      setErrMsg("Please enter a new maturity date.");
      return;
    }
    setErrMsg("");
    setSaving(true);

    try {
      let updateData = {};

      if (action === "settled") {
        updateData = {
          status:       "Settled",
          settled_date: settleDate ? toISO(settleDate) : new Date().toISOString().split("T")[0],
          settled_rate: settleAmt ? parseFloat(settleAmt) : null,
          notes:        note || null,
        };
      } else if (action === "rolled") {
        updateData = {
          status:          "Rolled Over",
          maturity_date:   toISO(rollDate),
          rate_locked_num: rollRate ? parseFloat(rollRate) : (hedge.rate_locked_num || null),
          notes:           `Rolled over. New maturity: ${rollDate}.${note ? " " + note : ""}`,
        };
      } else if (action === "extended") {
        updateData = {
          status:        "Extended",
          maturity_date: toISO(rollDate),
          notes:         note || null,
        };
      } else if (action === "cancelled") {
        updateData = {
          status: "Cancelled",
          notes:  note || null,
        };
      }

      const { error } = await supabase
        .from("hedge_positions")
        .update(updateData)
        .eq("id", hedge.id);

      if (error) {
        console.error("Hedge update failed:", error);
        setErrMsg(`Update failed: ${error.message}`);
        return;
      }

      onDone();
    } catch (e) {
      console.error("Action error:", e);
      setErrMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: "#0e1018", borderRadius: 8, border: "1px solid #2a2d3a", padding: "14px 16px", marginTop: 10 }}>
      <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 700 }}>
        Select Action
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {[
          { id: "settled",   label: "✓ Mark Settled",    color: "#059669", bg: "rgba(5,150,105,0.12)"  },
          { id: "rolled",    label: "↻ Roll Over",       color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
          { id: "extended",  label: "⟳ Extend Maturity", color: "#d97706", bg: "rgba(217,119,6,0.12)"  },
          { id: "cancelled", label: "✕ Cancel Contract", color: "#dc2626", bg: "rgba(220,38,38,0.12)"  },
        ].map(opt => (
          <button key={opt.id} onClick={() => { setAction(opt.id); setErrMsg(""); }} style={{
            padding: "6px 14px", borderRadius: 6,
            border: `1px solid ${opt.color}55`,
            background: action === opt.id ? opt.bg : "transparent",
            color: action === opt.id ? opt.color : "#64748b",
            fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
          }}>{opt.label}</button>
        ))}
      </div>

      {action === "settled" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>Settlement Rate</label>
            <input value={settleAmt} onChange={e => setSettleAmt(e.target.value)} placeholder="e.g. 84.25" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>Settlement Date (DD-MM-YYYY)</label>
            <input value={settleDate} onChange={e => setSettleDate(formatDateInput(e.target.value))} placeholder="DD-MM-YYYY" maxLength={10} style={inputStyle} />
          </div>
        </div>
      )}

      {(action === "rolled" || action === "extended") && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>New Maturity Date (DD-MM-YYYY) *</label>
            <input value={rollDate} onChange={e => setRollDate(formatDateInput(e.target.value))} placeholder="DD-MM-YYYY" maxLength={10} style={inputStyle} />
          </div>
          {action === "rolled" && (
            <div>
              <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>New Rate (optional)</label>
              <input value={rollRate} onChange={e => setRollRate(e.target.value)} placeholder="e.g. 84.50" style={inputStyle} />
            </div>
          )}
        </div>
      )}

      {action && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>Notes (optional)</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Any additional notes..." style={inputStyle} />
        </div>
      )}

      {errMsg && (
        <p style={{ fontSize: 11, color: "#f87171", margin: "0 0 10px" }}>{errMsg}</p>
      )}

      {action && (
        <button onClick={handleSubmit} disabled={saving} style={{
          padding: "8px 20px", borderRadius: 7, border: "none",
          background: saving ? "#374151" : "#6366f1",
          color: "#fff", fontSize: 12, fontWeight: 700,
          cursor: saving ? "not-allowed" : "pointer",
        }}>
          {saving ? "Saving…" : "Confirm Action"}
        </button>
      )}
    </div>
  );
}

// ── REC/PAY ACTION PANEL ──────────────────────────────────────────────────────
function RecPayActionPanel({ record, type, onDone }) {
  const [status,      setStatus]      = useState(record.status || "Outstanding");
  const [settledAmt,  setSettledAmt]  = useState(record.settled_amount || "");
  const [settledDate, setSettledDate] = useState("");
  const [saving,      setSaving]      = useState(false);
  const [errMsg,      setErrMsg]      = useState("");

  const totalAmt = parseAmt(record.amount);

  const handleSubmit = async () => {
    setErrMsg("");
    setSaving(true);
    try {
      const { error } = await supabase.from(type).update({
        status,
        settled_amount: settledAmt ? parseFloat(settledAmt) : null,
        settled_date:   settledDate ? toISO(settledDate) : null,
      }).eq("id", record.id);

      if (error) {
        console.error("Update failed:", error);
        setErrMsg(`Update failed: ${error.message}`);
        return;
      }
      onDone();
    } catch (e) {
      console.error(e);
      setErrMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: "#0e1018", borderRadius: 8, border: "1px solid #2a2d3a", padding: "14px 16px", marginTop: 10 }}>
      <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 700 }}>
        Update Status
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>Status *</label>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, appearance: "auto" }}>
            <option>Outstanding</option>
            <option>Overdue</option>
            <option>Partially Settled</option>
            <option>Settled</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>
            Amount Settled {totalAmt > 0 && `(of ${fmtAmt(totalAmt, record.currency)})`}
          </label>
          <input value={settledAmt} onChange={e => setSettledAmt(e.target.value)}
            placeholder={`e.g. ${totalAmt}`} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>Settlement Date (DD-MM-YYYY)</label>
          <input value={settledDate} onChange={e => setSettledDate(formatDateInput(e.target.value))}
            placeholder="DD-MM-YYYY" maxLength={10} style={inputStyle} />
        </div>
      </div>

      {settledAmt && totalAmt > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#64748b", marginBottom: 4 }}>
            <span>Settlement Progress</span>
            <span>{Math.min(100, ((parseFloat(settledAmt) / totalAmt) * 100)).toFixed(1)}%</span>
          </div>
          <div style={{ background: "#2a2d3a", borderRadius: 4, height: 6 }}>
            <div style={{
              height: 6, borderRadius: 4, background: "#059669",
              width: `${Math.min(100, (parseFloat(settledAmt) / totalAmt) * 100)}%`,
              transition: "width 0.3s",
            }} />
          </div>
        </div>
      )}

      {errMsg && (
        <p style={{ fontSize: 11, color: "#f87171", margin: "0 0 10px" }}>{errMsg}</p>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{
        padding: "8px 20px", borderRadius: 7, border: "none",
        background: saving ? "#374151" : "#059669",
        color: "#fff", fontSize: 12, fontWeight: 700,
        cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Saving…" : "Update"}
      </button>
    </div>
  );
}

// ── ALERT ITEM ────────────────────────────────────────────────────────────────
function AlertItem({ item, companyId, onDone }) {
  const [expanded, setExpanded] = useState(false);
  const [done,     setDone]     = useState(false);

  if (done) return (
    <div style={{
      background: "rgba(5,150,105,0.08)", border: "1px solid rgba(5,150,105,0.2)",
      borderRadius: 10, padding: "12px 16px",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <CheckCircle size={16} color="#4ade80" />
      <span style={{ fontSize: 13, color: "#4ade80", fontWeight: 600 }}>
        {item.label} — Updated ✓
      </span>
    </div>
  );

  const isOverdue = item.daysLate > 0;
  const urgency = isOverdue
    ? { color: "#f87171", bg: "rgba(248,113,113,0.08)", border: "rgba(248,113,113,0.2)", icon: <AlertTriangle size={16} color="#f87171" /> }
    : { color: "#fbbf24", bg: "rgba(251,191,36,0.08)",  border: "rgba(251,191,36,0.2)",  icon: <Clock size={16} color="#fbbf24" /> };

  return (
    <div style={{ background: urgency.bg, border: `1px solid ${urgency.border}`, borderRadius: 10, overflow: "hidden" }}>
      <div onClick={() => setExpanded(e => !e)} style={{
        padding: "12px 16px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        {urgency.icon}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>{item.label}</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
              background: urgency.bg, color: urgency.color, border: `1px solid ${urgency.border}`,
            }}>
              {item.daysLate > 0 ? `${item.daysLate}d overdue` : "Due today"}
            </span>
            <span style={{
              fontSize: 10, padding: "2px 8px", borderRadius: 20,
              background: "#1a1d26", color: "#64748b", border: "1px solid #2a2d3a",
            }}>{item.type}</span>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>{item.detail}</div>
        </div>
        <div style={{ color: "#64748b", flexShrink: 0 }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 16px 16px" }}>
          {item.category === "hedge" ? (
            <HedgeActionPanel
              hedge={item.record}
              companyId={companyId}
              onDone={() => { setDone(true); onDone(); }}
            />
          ) : (
            <RecPayActionPanel
              record={item.record}
              type={item.category === "receivable" ? "receivables" : "payables"}
              onDone={() => { setDone(true); onDone(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── MAIN MODAL ────────────────────────────────────────────────────────────────
export default function MaturityAlertModal({ hedges, receivables, payables, companyId, onDataRefresh }) {
  const [alerts,  setAlerts]  = useState([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const now = todayDate();
    const items = [];

    // ── Hedge contracts: matured or due today ─────────────────────────────
    (hedges || []).forEach(h => {
      const matDate = h.maturity_date || h.expiry_date;
      if (!matDate) return;

      // Skip already actioned — treat null/undefined/empty as "Active"
      const hStatus = (h.status || "Active").trim();
      if (ARCHIVED_STATUSES.includes(hStatus)) return;

      const matD     = new Date(matDate); matD.setHours(0, 0, 0, 0);
      const daysLate = Math.round((now - matD) / 86400000);
      if (daysLate >= 0) {
        items.push({
          id:       h.id,
          category: "hedge",
          type:     h.instrument_type || "Hedge",
          label:    h.contract_reference || "Hedge Contract",
          daysLate,
          detail:   `${h.currency_pair || ""} · ${fmtAmt(h.notional_amount_num)} · Matured: ${fmtDate(matDate)} · Bank: ${h.counterparty_bank || "—"}`,
          record:   h,
        });
      }
    });

    // ── Receivables: overdue or due today ─────────────────────────────────
    (receivables || []).forEach(r => {
      if (!r.due_date) return;
      const rStatus = (r.status || "Outstanding").trim();
      if (rStatus === "Settled") return;

      const dueD     = new Date(r.due_date); dueD.setHours(0, 0, 0, 0);
      const daysLate = Math.round((now - dueD) / 86400000);
      if (daysLate >= 0) {
        items.push({
          id:       r.id,
          category: "receivable",
          type:     "Receivable",
          label:    `${r.party_name || "Receivable"} — ${r.invoice_number || ""}`,
          daysLate,
          detail:   `${r.currency || "INR"} ${fmtAmt(parseAmt(r.amount), r.currency || "INR")} · Due: ${fmtDate(r.due_date)} · Status: ${rStatus}`,
          record:   r,
        });
      }
    });

    // ── Payables: overdue or due today ────────────────────────────────────
    (payables || []).forEach(p => {
      if (!p.due_date) return;
      const pStatus = (p.status || "Outstanding").trim();
      if (pStatus === "Settled") return;

      const dueD     = new Date(p.due_date); dueD.setHours(0, 0, 0, 0);
      const daysLate = Math.round((now - dueD) / 86400000);
      if (daysLate >= 0) {
        items.push({
          id:       p.id,
          category: "payable",
          type:     "Payable",
          label:    `${p.party_name || "Payable"} — ${p.invoice_number || ""}`,
          daysLate,
          detail:   `${p.currency || "INR"} ${fmtAmt(parseAmt(p.amount), p.currency || "INR")} · Due: ${fmtDate(p.due_date)} · Status: ${pStatus}`,
          record:   p,
        });
      }
    });

    // Sort: most overdue first
    items.sort((a, b) => b.daysLate - a.daysLate);

    setAlerts(items);
    setVisible(items.length > 0);
  }, [hedges, receivables, payables]);

  if (!visible || alerts.length === 0) return null;

  const urgentCount = alerts.filter(a => a.daysLate > 0).length;
  const todayCount  = alerts.filter(a => a.daysLate === 0).length;

  return (
    <>
      {/* Backdrop */}
      <div onClick={() => setVisible(false)} style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
        zIndex: 9998,
      }} />

      {/* Modal */}
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(760px, 95vw)", maxHeight: "85vh",
        background: "#12141a", border: "1px solid #2a2d3a",
        borderRadius: 14, boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        zIndex: 9999, display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>

        {/* Header */}
        <div style={{
          padding: "18px 22px", borderBottom: "1px solid #2a2d3a",
          display: "flex", alignItems: "center", gap: 14,
          background: "#0e1018", flexShrink: 0,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 9,
            background: "rgba(248,113,113,0.12)",
            border: "1px solid rgba(248,113,113,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <AlertTriangle size={20} color="#f87171" />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
              Action Required — {alerts.length} Alert{alerts.length > 1 ? "s" : ""}
            </h2>
            <p style={{ fontSize: 11, color: "#64748b", margin: "3px 0 0" }}>
              {urgentCount > 0 && `${urgentCount} overdue · `}
              {todayCount  > 0 && `${todayCount} due today · `}
              Click each item to take action
            </p>
          </div>

          {/* Summary pills */}
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            {alerts.filter(a => a.category === "hedge").length > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "rgba(99,102,241,0.12)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.25)" }}>
                {alerts.filter(a => a.category === "hedge").length} Hedge
              </span>
            )}
            {alerts.filter(a => a.category === "receivable").length > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "rgba(74,222,128,0.1)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.2)" }}>
                {alerts.filter(a => a.category === "receivable").length} Receivable
              </span>
            )}
            {alerts.filter(a => a.category === "payable").length > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "rgba(248,113,113,0.1)", color: "#f87171", border: "1px solid rgba(248,113,113,0.2)" }}>
                {alerts.filter(a => a.category === "payable").length} Payable
              </span>
            )}
          </div>

          <button onClick={() => setVisible(false)} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#64748b", padding: 4, flexShrink: 0,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Alert list */}
        <div style={{ overflowY: "auto", padding: "16px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
          {alerts.map(alert => (
            <AlertItem
              key={`${alert.category}-${alert.id}`}
              item={alert}
              companyId={companyId}
              onDone={() => { if (onDataRefresh) onDataRefresh(); }}
            />
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 22px", borderTop: "1px solid #2a2d3a",
          background: "#0e1018", display: "flex",
          alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <p style={{ fontSize: 11, color: "#374151", margin: 0 }}>
            Updates save immediately to Supabase.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { if (onDataRefresh) onDataRefresh(); }} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 14px", borderRadius: 7,
              border: "1px solid #2a2d3a", background: "transparent",
              color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>
              <RefreshCw size={13} /> Refresh Data
            </button>
            <button onClick={() => setVisible(false)} style={{
              padding: "7px 18px", borderRadius: 7, border: "none",
              background: "#6366f1", color: "#fff",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
